/**
 * React dependencies
 */

import React, { useContext, useEffect, useState } from 'react';

import { trackAudioPlay, trackBookInfoClick } from '../../analytics';
import { infoIcon, pauseButton, playButton, shareIcon } from '../../img/icons';
import { ModalContext } from '../Modal/ModalContext';
import { useSwiperSlide } from 'swiper/react';

/**
 * Audio Player
 *
 * A component to render a single audio book
 *
 * @param {Object}  props
 * @param {Object}  props.isbn
 * @param {String}  props.altText
 * @param {String}  props.format
 * @param {String}  props.coverUrl
 * @param {Boolean}  props.isAutomatic
 */
const AudioPlayer = ({ item, altText, format, coverUrl, context }) => {
  const isAutomatic = context?.entry === 'automatic';
  const [ isPlaying, setIsPlaying ] = useState( false );
  const [ isLoading, setIsLoading ] = useState( false );
  const [ progress, setProgress ] = useState( 0 );
  const [ audio, setAudio ] = useState( null );
  const [ hasExcerpt ] = useState(
    isAutomatic ? ( !!( item.asset_types && item.asset_types[18] ) ) : undefined
  );
  const { onClickOpenModal } = useContext( ModalContext );
  // simplify isbn variable
  const isbn = item?.isbnStr || item?.isbn;
  // calculate radius and circumference for the loading circle
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const swiperSlide = useSwiperSlide();

  useEffect( () => {
    if ( audio ) return;
    const existingAudio = document.getElementById( `audio-${item.isbn}` );
    if ( !existingAudio ) {
      let newAudio = document.createElement( 'audio' );
      newAudio.id = `audio-${isbn}`;
      document.body.appendChild( newAudio );
      setAudio( newAudio );
    } else {
      setAudio( existingAudio );
    }
  });

  useEffect( () => {
    if ( !audio ) return;
    setIsPlaying( !audio.paused );
    setProgress( ( audio.currentTime / audio.duration ) * 100 );
    setupAudioEventListeners();
  }, [ audio ] );

  const togglePlayPause = async() => {
    if ( !audio ) return;
    if ( !audio.src ) {
      pauseAllAudio();
      let url;
      if ( item?.audioUrl ) {
        url = item.audioUrl;
      } else {
        url = await fetchAssetUrl();
      }
      if ( url ) {
        audio.src = url;
        audio.play();
        setIsPlaying( true );
      }
    } else {
      if ( audio.paused ) {
        pauseAllAudio();
        audio.play();
        setIsPlaying( true );
        if ( audio.currentTime === 0 ) {
          setLoading();
        }
      } else {
        audio.pause();
        setIsPlaying( false );
      }
    }
  };

  const pauseAllAudio = () => {
    const audioPlayers = document.getElementsByTagName( 'audio' );
    if ( audioPlayers.length ) {
      for ( let i = 0; i < audioPlayers.length; i++ ) {
        if ( !audioPlayers[i].paused ) audioPlayers[i].pause();
      }
    }
  };

  const fetchAssetUrl = async() => {
    if ( isbn && hasExcerpt ) {
      try {
        setIsLoading( true );
        const response = await fetch( '/wp-json/dwt/v2/audio-mp3', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            asset_type: 18,
            isbn
          })
        });
        const data = await response.json();
        const result = data?.result[0]?.asset_types[0]?.assets[0]?.access?.url;
        return result;
      } catch ( error ) {}
    }
    return;
  };

  const setupAudioEventListeners = () => {
    if ( !audio ) return;
    const progressTracker = () => setProgress( ( audio.currentTime / audio.duration ) * 100 );
    audio.addEventListener( 'timeupdate', progressTracker );
    audio.addEventListener( 'ended', () => setIsPlaying( false ) );
    audio.addEventListener( 'pause', () => setIsPlaying( false ) );
    audio.addEventListener( 'loadeddata', () => setIsLoading( false ) );
  };

  const setLoading = () => {
    setIsLoading( true );
    trackAudioPlay( item, isAutomatic );
  };

  const titleDecode = ( title ) => {
    let decodedTitle;
    try {
      decodedTitle = decodeURIComponent( title );
    } catch ( error ) {
      decodedTitle = title;
    }
    return decodedTitle;
  };

  return (
    <>
      <div className="audio-book">
        <div className="image-wrapper is-audio-book">
          <div className="api-image-wrapper">
            <img
              className={`api-image ${isLoading && 'is-loading'}`}
              src={coverUrl}
              alt={altText}
              data-format={format}
              data-isbn={item?.isbn}
              data-workid={item?.workId}
              title={titleDecode( item?.title )}
            />
            {isLoading && (
              <svg className="loading-circle" width="100" height="100">
                <circle
                  r={radius}
                  cx="50"
                  cy="50"
                  fill="transparent"
                  stroke="#000"
                  strokeWidth="10"
                />
                <circle
                  r={radius}
                  cx="50"
                  cy="50"
                  fill="transparent"
                  stroke="#fff"
                  strokeWidth="10"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference * 0.75}
                  style={{
                    animation: 'rotate 2s linear infinite',
                    transformOrigin: 'center'
                  }}
                />
              </svg>
            )}
          </div>
          <div className="icon-wrapper">
            <button
              aria-label={ `Info Modal - ${titleDecode( item?.title )}` }
              data-testid="info-modal-btn"
              tabIndex={ swiperSlide ? swiperSlide.isVisible ? 0 : -1 : 0 }
              className="icon"
              onClick={() => {
                onClickOpenModal( isbn, 'books' );
                trackBookInfoClick( item, context );
              }
              }
            >
              {infoIcon}
            </button>
            {( isAutomatic ? hasExcerpt : item?.audioUrl ) && (
              <>
                <button
                  aria-label={ `${isPlaying ? 'Pause' : 'Play'} Clip - ${titleDecode( item?.title )}` }
                  tabIndex={ swiperSlide ? swiperSlide.isVisible ? 0 : -1 : 0 }
                  className="icon"
                  data-testid="play-pause-btn"
                  onClick={() => togglePlayPause()}
                >
                  {isPlaying ? pauseButton : playButton}
                </button>
                <button
                  aria-label={ `Share Modal - ${titleDecode( item?.title )}` }
                  tabIndex={ swiperSlide ? swiperSlide.isVisible ? 0 : -1 : 0 }
                  className="icon"
                  data-testid="share-modal-btn"
                  onClick={() =>
                    onClickOpenModal( isbn, 'share' )
                  }
                >
                  {shareIcon}
                </button>
              </>
            )}
          </div>
          <progress
            data-testid="player-progress"
            className={`player-progress ${
              ( progress > 0 ) && 'playing'
            }`}
            value={isNaN( progress ) ? 0 : progress}
            max={100}
          />
        </div>
      </div>
    </>
  );
};

export default AudioPlayer;
